import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  Paper,
  CircularProgress
} from '@mui/material';
import closeIcon from "../../assets/icons/Close Square.svg";
import FileDownloadIcon from '@mui/icons-material/FileDownload';

interface DocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  imageName: string;
  fileType?: 'image' | 'pdf' | 'video' | 'document' | 'spreadsheet' | 'presentation' | 'archive' | 'other';
}

const DocumentViewerModal = ({
  open,
  onClose,
  fileName,
  imageName,
  fileType = 'image'
}: DocumentViewerModalProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Clean up object URL when component unmounts or URL changes
  useEffect(() => {
    return () => {
      if (fileName && fileName.startsWith('blob:')) {
        URL.revokeObjectURL(fileName);
      }
    };
  }, [fileName]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
    }
  }, [open, fileName]);

  const handleDownloadView = () => {
    try {
      const link = document.createElement('a');
      link.href = fileName;

      const fileExtension = imageName.split('.').pop() || 'jpg';
      const downloadName = imageName || `document.${fileExtension}`;

      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed. Please try again.');
    }
  };

  const handleLoad = () => {
    setLoading(false);
    setError(null);
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load document. The file may be corrupted or unavailable.');
  };

  const handleElementContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Function to determine if file type should show preview
  const shouldShowPreview = () => {
    return ['image', 'pdf', 'video'].includes(fileType);
  };

  // Function to determine if file type should show download only message
  const shouldShowDownloadOnly = () => {
    return ['document', 'spreadsheet', 'presentation', 'archive', 'other'].includes(fileType);
  };

  const renderContent = () => {
    if (error) {
      return (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography color="error" variant="body1">
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={handleDownloadView}
            sx={{ mt: 2 }}
          >
            Try Download Instead
          </Button>
        </Box>
      );
    }

    return (
      <>
        {loading && shouldShowPreview() && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading document...
            </Typography>
          </Box>
        )}

        {fileType === 'image' && (
          <Box
            component="img"
            src={fileName}
            alt={imageName}
            onLoad={handleLoad}
            onError={handleError}
            onContextMenu={handleElementContextMenu}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: '16px',
              display: loading ? 'none' : 'block'
            }}
          />
        )}

        {fileType === 'pdf' && (
          <iframe
            src={fileName}
            title={imageName}
            width="100%"
            height="100%"
            onLoad={handleLoad}
            onError={handleError}
            onContextMenu={handleElementContextMenu}
            style={{
              border: 'none',
              borderRadius: '16px',
              display: loading ? 'none' : 'block'
            }}
          />
        )}

        {fileType === 'video' && (
          <video
            src={fileName}
            autoPlay
            muted
            onLoadedData={handleLoad}
            onError={handleError}
            onContextMenu={handleElementContextMenu}
            style={{
              borderRadius: '16px',
              display: loading ? 'none' : 'block',
              objectFit: 'contain'
            }}
          />
        )}

        {shouldShowDownloadOnly() && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              onContextMenu={handleElementContextMenu}
              sx={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                mb: 2
              }}
            >
              Preview not available for {fileType} files. Please download to view this file.
            </Typography>
            <Button
              variant="contained"
              onClick={handleDownloadView}
              startIcon={<FileDownloadIcon />}
            >
              Download {fileType}
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100vw - 64px)',
          maxWidth: 800,
          height: 'calc(100vh - 64px)',
          maxHeight: 583,
          gap: '32px',
          borderRadius: '16px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{imageName}</Typography>
          <IconButton onClick={onClose}>
            <img src={closeIcon} width={24} height={24} alt="close" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '16px',
            border: '1px solid rgba(238, 237, 252, 1)',
            height: '360px',
            width: '100%',
            overflow: 'hidden',
            backgroundColor: '#f9f9f9',
            position: 'relative'
          }}
        >
          {renderContent()}
        </Box>

        {/* Download Button - Always show for all file types */}
        <Box textAlign="end" mt={2}>
          <Button
            onClick={handleDownloadView}
            variant='contained'
            sx={{ background: "#6c63ff" }}
            startIcon={<FileDownloadIcon />}
            disabled={!!error}
          >
            Download
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default DocumentViewerModal;